import { useState } from "react";
import { createFileRoute, Link } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/api";
import type { AccountLimits, AdminOrganisation, AuditEntry, AccountPackage } from "@/lib/types";
import { Figure, Panel, FigureRow } from "@/ui/Panel";
import { Head } from "@/ui/frame/Page";
import { Trail } from "@/ui/Trail";
import { DeleteOrganisationPanel } from "@/features/admin/DeleteOrganisationPanel";
import { OrganisationBillingPanel } from "@/features/admin/OrganisationBillingPanel";
import {
  OrganisationPeoplePanel,
  OrganisationServicesPanel,
} from "@/features/admin/OrganisationPeoplePanel";
import { Button } from "@/ui/Button";
import { Chip } from "@/ui/Chip";
import { Field, Input } from "@/ui/Field";
import { Choice } from "@/ui/Choice";
import { Table, Td, Th, Tr } from "@/ui/Table";
import { EmptyState, ErrorState, Skeleton, TableSkeleton } from "@/ui/States";
import { useToast } from "@/ui/Toast";
import { cx, absoluteTime, humaniseAction, relativeTime, shortTime } from "@/lib/format";
import { PACKAGE_LABEL, PACKAGE_TONE } from "@/features/admin/packages";
import { packageIn, usePackages } from "@/features/plan/usePackages";

export const Route = createFileRoute("/_app/admin/$orgId")({
  component: AdminOrganisationScreen,
});

function AdminOrganisationScreen() {
  const { orgId } = Route.useParams();
  const queryClient = useQueryClient();

  const detail = useQuery({
    queryKey: ["platform-organisation", orgId],
    queryFn: ({ signal }) =>
      api.get<{ organisation: AdminOrganisation; account: AccountLimits }>(
        `/admin/organisations/${orgId}`,
        undefined,
        signal,
      ),
  });

  const activity = useQuery({
    queryKey: ["platform-organisation-activity", orgId],
    queryFn: ({ signal }) =>
      api.get<{ entries: AuditEntry[] | null }>(
        "/admin/audit-log",
        { organisation_id: orgId, limit: 25 },
        signal,
      ),
  });

  const refresh = async () => {
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: ["platform-organisation", orgId] }),
      queryClient.invalidateQueries({ queryKey: ["platform-organisation-activity", orgId] }),
      queryClient.invalidateQueries({ queryKey: ["platform-organisations"] }),
    ]);
  };

  if (detail.isLoading) {
    return (
      <div className="flex flex-col gap-4">
        <Skeleton className="h-7 w-56" />
        <Skeleton className="h-40 w-full rounded-xl" />
      </div>
    );
  }
  if (detail.isError) {
    return <ErrorState error={detail.error} onRetry={() => void detail.refetch()} />;
  }

  const org = detail.data!.organisation;
  const account = detail.data!.account;

  return (
    <div className="flex flex-col gap-6">
      <Head
        above={
          <Trail
            trail={[
              { label: "Organisations", to: "/admin/organisations" },
              { label: org.display_name },
            ]}
          />
        }
        title={
          <span className="flex flex-wrap items-center gap-3">
            {org.display_name}
            <Chip tone={PACKAGE_TONE[org.package]}>{PACKAGE_LABEL[org.package]}</Chip>
            {org.status === "suspended" && <Chip tone="down">Suspended</Chip>}
            {/* The rental, beside the plan it is for.
                "Why are this customer's services off" used to be answered by a
                suspension reason or an unpaid invoice. It is now usually
                answered by a date, and a support person reading this header
                should not have to open another screen to find it. */}
            {org.lapsed_at ? (
              <Chip tone="down">Rental ran out</Chip>
            ) : !org.paid_through ? (
              <Chip>Never rented</Chip>
            ) : null}
          </span>
        }
        description={
          <span className="flex flex-wrap items-baseline gap-x-4 gap-y-1">
            <span>{org.owner_email ? `Owned by ${org.owner_email}` : org.slug}</span>
            <span className="t-meta">
              {org.paid_through
                ? `Rented through ${new Date(org.paid_through).toLocaleDateString(undefined, { day: "numeric", month: "long", year: "numeric" })}`
                : "Nothing rented"}
              {" · "}
              {org.balance} of credit
            </span>
          </span>
        }
        actions={
          <Link to="/admin">
            <Button tone="ghost">All organisations</Button>
          </Link>
        }
      />

      {org.status === "suspended" && org.suspension_reason && (
        <Panel>
          <p className="text-sm text-[var(--text-primary)]">Suspended: {org.suspension_reason}</p>
          <p className="mt-0.5 text-[13px] text-[var(--text-muted)]">
            {relativeTime(org.suspended_at)}. The customer is shown this reason.
          </p>
        </Panel>
      )}

      {/* A rail, like every other set of figures in the product. These were
          three bordered tiles holding two words each — the tile pattern the
          rest of the rebuild removed, left standing on the one screen that
          had hand-rolled its own. */}
      <FigureRow>
        <Figure label="People" value={org.members.toLocaleString()} />
        <Figure label="Projects" value={org.projects.toLocaleString()} />
        <Figure
          label="Here since"
          value={relativeTime(org.created_at)}
          hint={absoluteTime(org.created_at)}
        />
      </FigureRow>

      <SuspensionPanel org={org} onDone={refresh} />
      <TierPanel org={org} account={account} onDone={refresh} />

      <OrganisationBillingPanel orgId={orgId} org={org} />

      <OrganisationPeoplePanel orgId={orgId} />

      <OrganisationServicesPanel orgId={orgId} />

      <Panel
        title="What has happened here"
        description="Including what we did. This is the same log the customer reads."
        padded={false}
      >
        {activity.isLoading ? (
          <div className="px-5 py-4">
            <TableSkeleton rows={4} columns={3} />
          </div>
        ) : (activity.data?.entries ?? []).length === 0 ? (
          <div className="px-5 py-4">
            <EmptyState title="Nothing yet" description="Actions appear here as they happen." />
          </div>
        ) : (
          <div className="px-5 py-4">
            <Table dense fixed>
              <thead>
                <Tr>
                  <Th width="w-[40%]">What</Th>
                  <Th width="w-[24%]">Who</Th>
                  <Th width="w-[22%]">Why</Th>
                  <Th width="w-[14%]" align="right">When</Th>
                </Tr>
              </thead>
              <tbody>
                {(activity.data?.entries ?? []).map((entry) => (
                  <Tr key={entry.id}>
                    {/* What it acted on, beside the action rather than under
                        it. Without the target the column reads "Created" five
                        times in a row and tells nobody anything — which is what
                        this screen did, while the customer's own log and the
                        platform-wide one both showed it. The reason used to be
                        a third line; it has its own column now. */}
                    <Td
                      clip
                      title={entry.action}
                      secondary={entry.target_label || entry.target_type || undefined}
                    >
                      <span className="text-[var(--text-primary)]">
                        {humaniseAction(entry.action)}
                      </span>
                    </Td>
                    <Td clip title={entry.actor_label || entry.actor_type}>
                      <span className="text-[var(--text-secondary)]">
                        {entry.actor_label || entry.actor_type.replace(/_/g, " ")}
                      </span>
                    </Td>
                    <Td clip title={entry.reason || undefined}>
                      <span className="text-[var(--text-secondary)]">{entry.reason || "—"}</span>
                    </Td>
                    <Td align="right" title={absoluteTime(entry.created_at)}>
                      {shortTime(entry.created_at)}
                    </Td>
                  </Tr>
                ))}
              </tbody>
            </Table>
          </div>
        )}
      </Panel>

      <DeleteOrganisationPanel orgId={orgId} name={org.display_name} />
    </div>
  );
}

function SuspensionPanel({
  org,
  onDone,
}: {
  org: AdminOrganisation;
  onDone: () => Promise<void>;
}) {
  const toast = useToast();
  const [reason, setReason] = useState("");

  const suspend = useMutation({
    mutationFn: () =>
      api.post<void>(`/admin/organisations/${org.id}/suspend`, { reason: reason.trim() }),
    onSuccess: async () => {
      toast.success("Suspended", "Everything this organisation was running has been stopped.");
      setReason("");
      await onDone();
    },
    onError: toast.error,
  });

  const reactivate = useMutation({
    mutationFn: () => api.post<void>(`/admin/organisations/${org.id}/reactivate`),
    onSuccess: async () => {
      toast.success("Reactivated");
      await onDone();
    },
    onError: toast.error,
  });

  if (org.status === "suspended") {
    return (
      <Panel
        title="Start this organisation again"
        description="Everything that was running when it was suspended starts again."
      >
        <Button tone="primary" size="dense" busy={reactivate.isPending} onClick={() => reactivate.mutate()}>
          Reactivate
        </Button>
      </Panel>
    );
  }

  return (
    <Panel
      title="Suspend this organisation"
      description="Stops everything it is running. The reason is required, and the customer is shown it."
    >
      <div className="flex flex-wrap items-end gap-3">
        <div className="min-w-64 flex-1">
          <Field label="Reason" required>
            {({ id }) => (
              <Input
                id={id}
                value={reason}
                placeholder="Why this account is being stopped"
                onChange={(e) => setReason(e.target.value)}
              />
            )}
          </Field>
        </div>
        <Button
          tone="danger"
          size="dense"
          busy={suspend.isPending}
          disabled={reason.trim().length < 3}
          onClick={() => suspend.mutate()}
        >
          Suspend
        </Button>
      </div>
    </Panel>
  );
}

function TierPanel({
  org,
  account,
  onDone,
}: {
  org: AdminOrganisation;
  account: AccountLimits;
  onDone: () => Promise<void>;
}) {
  const toast = useToast();
  const [chosen, setChosen] = useState<AccountPackage>(org.package);
  const [reason, setReason] = useState("");

  const change = useMutation({
    mutationFn: () =>
      api.put<void>(`/admin/organisations/${org.id}/package`, {
        package: chosen,
        reason: reason.trim(),
      }),
    onSuccess: async () => {
      toast.success(`Now ${PACKAGE_LABEL[chosen].toLowerCase()}`);
      setReason("");
      await onDone();
    },
    onError: toast.error,
  });

  // The catalogue, rather than a copy of it kept here.
  //
  // This screen had its own sentence per package and the customer's own
  // screen had another, both abbreviated and each leaving out different
  // ceilings. An operator changing somebody's package was reading a
  // description that did not say what it would actually do to their disk or
  // their domains.
  const catalogue = usePackages();
  const here = packageIn(catalogue.data, org.package);
  const options = catalogue.data?.packages ?? [];

  return (
    <Panel
      title="Package"
      description={
        here
          ? `${here.summary} ${here.monthly} a month when the customer rents it.`
          : undefined
      }

      footer={
        <div className="flex flex-wrap items-end gap-3">
          <Field label="Change to">
            {({ id }) => (
              <Choice
                id={id}
                value={chosen}
                onValueChange={(v) => setChosen(v as AccountPackage)}
                options={options.map((pkg) => ({
                  value: pkg.name,
                  label: pkg.label,
                  // What it allows and what it can come to, so a change is
                  // made on the numbers rather than on the name.
                  description: `${allowanceLine(pkg.limits)} · ${pkg.monthly} a month`,
                }))}
              />
            )}
          </Field>
          <div className="min-w-56 flex-1">
            <Field label="Why">
              {({ id }) => (
                <Input
                  id={id}
                  value={reason}
                  placeholder="Recorded, and the customer reads it"
                  onChange={(e) => setReason(e.target.value)}
                />
              )}
            </Field>
          </div>
          <Button
            tone="secondary"
            size="dense"
            busy={change.isPending}
            disabled={chosen === org.package}
            onClick={() => change.mutate()}
          >
            Change
          </Button>
        </div>
      }
    >
      {/* What this control now does, said out loud.
          Customers change their own plan and pay the difference out of their
          balance. This does not: it moves the tier and takes nothing, which is
          how the unlimited tier is granted and how a complaint gets settled.
          They keep the new ceilings until their rental ends, and the next
          extension is charged at the new plan's price — so a generous change
          here is temporary generosity rather than a new price. */}
      <p className="t-meta mb-3">
        Changing it here takes nothing from the customer&rsquo;s balance. They keep
        the new ceilings until their rental ends; the next extension is charged at
        the new plan&rsquo;s price.
      </p>

      <div className="grid gap-x-10 sm:grid-cols-2">
        <Allowance label="Services" used={account.usage.services} limit={account.limits.services} />
        <Allowance label="Databases" used={account.usage.databases} limit={account.limits.databases} />
        <Allowance label="Domains" used={account.usage.domains} limit={account.limits.domains} />
        <Allowance
          label="Builds this hour"
          used={account.usage.builds_per_hour}
          limit={account.limits.builds_per_hour}
        />
        <Allowance
          label="Processor, cores"
          used={Math.round(account.usage.total_cpu_millicores / 100) / 10}
          limit={Math.round(account.limits.total_cpu_millicores / 100) / 10}
        />
        <Allowance
          label="Memory, GiB"
          used={Math.round(account.usage.total_memory_mib / 1024)}
          limit={Math.round(account.limits.total_memory_mib / 1024)}
        />
      </div>
    </Panel>
  );
}


/**
 * One ceiling, as a row rather than a tile.
 *
 * Six bordered boxes in a three-by-two grid is a lot of furniture for six
 * pairs of numbers, and the one that matters — a ceiling that has been
 * reached — looked exactly like the five that had not. Rows separated by
 * hairlines, the figure right-aligned so the column reads down, and the
 * colour spent only on the one that is full.
 */
function Allowance({ label, used, limit }: { label: string; used: number; limit: number }) {
  const full = limit > 0 && used >= limit;
  return (
    <div className="flex items-baseline justify-between gap-4 border-b border-[var(--border)] py-2 last:border-b-0">
      <span className="section-label">{label}</span>
      <span
        className={cx(
          "text-[13px] tabular-nums",
          full ? "text-[var(--danger)]" : "text-[var(--text-primary)]",
        )}
      >
        {used.toLocaleString()} of {limit.toLocaleString()}
      </span>
    </div>
  );
}


/** Every ceiling on one line, for a chooser that has to fit in a menu. */
function allowanceLine(l: {
  services: number;
  databases: number;
  domains: number;
  total_cpu_millicores: number;
  total_memory_mib: number;
  total_storage_gib: number;
}): string {
  return [
    `${l.services} services`,
    `${l.databases} databases`,
    `${l.domains} domains`,
    `${(l.total_cpu_millicores / 1000).toFixed(0)} cores`,
    `${(l.total_memory_mib / 1024).toFixed(0)} GiB`,
    `${l.total_storage_gib} GiB disk`,
  ].join(" · ");
}
